import { useEffect, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { usePopover } from './usePopover'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const pad = (value: number) => String(value).padStart(2, '0')

/** `2026-09-23T14:05` in local time, the same value a datetime-local input uses. */
export function toLocalDateTimeValue(date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function parseLocalDateTime(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const [, year, month, day, hour, minute] = match.map(Number)
  const date = new Date(year, month - 1, day, hour, minute)
  return Number.isNaN(date.getTime()) ? null : date
}

const dayKey = (date: Date) => toLocalDateTimeValue(date).slice(0, 10)
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)

/** One two-digit time segment: type a value, or step it with the arrow keys. */
function TimeSegment({
  label,
  value,
  min,
  max,
  onCommit,
}: {
  label: string
  value: number
  min: number
  max: number
  onCommit: (value: number) => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const wrap = (next: number) => (next > max ? min : next < min ? max : next)

  return (
    <input
      aria-label={label}
      className="wt-time-segment"
      inputMode="numeric"
      maxLength={2}
      onBlur={() => setDraft(null)}
      onChange={(event) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 2)
        setDraft(digits)
        const next = Number(digits)
        if (digits && next >= min && next <= max) onCommit(next)
      }}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        event.preventDefault()
        setDraft(null)
        onCommit(wrap(value + (event.key === 'ArrowUp' ? 1 : -1)))
      }}
      value={draft ?? pad(value)}
    />
  )
}

/**
 * Compact date and time field. The value is the local `YYYY-MM-DDTHH:mm`
 * string a datetime-local input would produce, so callers parse it unchanged.
 * With `dateOnly` only the day is shown and chosen; the time part of the
 * value is kept as it was.
 */
export function DateTimePicker({
  id,
  value,
  onChange,
  dateOnly = false,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  dateOnly?: boolean
}) {
  const popover = usePopover<HTMLButtonElement>({ minWidth: 292 })
  const selected = parseLocalDateTime(value)
  const [view, setView] = useState(() => monthStart(selected ?? new Date()))
  const [focusDay, setFocusDay] = useState(() => selected ?? new Date())
  const [moveFocus, setMoveFocus] = useState(false)
  const today = new Date()

  useEffect(() => {
    if (!moveFocus || !popover.isOpen) return
    popover.panelRef.current?.querySelector<HTMLButtonElement>(`[data-day="${dayKey(focusDay)}"]`)?.focus()
    setMoveFocus(false)
  }, [moveFocus, focusDay, popover.isOpen, popover.panelRef])

  // Missing or unparseable values fall back to now, so every edit yields a full value.
  const base = selected ?? new Date()
  const hours = base.getHours()
  const hour12 = hours % 12 || 12
  const pm = hours >= 12

  function commit(date: Date) {
    onChange(toLocalDateTimeValue(date))
  }

  function withTime(day: Date, hour24: number, minute: number) {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour24, minute)
  }

  function openPicker() {
    const start = selected ?? new Date()
    setView(monthStart(start))
    setFocusDay(start)
    setMoveFocus(true)
    popover.open()
  }

  function focusOn(day: Date) {
    setFocusDay(day)
    setView(monthStart(day))
    setMoveFocus(true)
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focusDay, -1),
      ArrowRight: () => addDays(focusDay, 1),
      ArrowUp: () => addDays(focusDay, -7),
      ArrowDown: () => addDays(focusDay, 7),
      PageUp: () => new Date(focusDay.getFullYear(), focusDay.getMonth() - 1, Math.min(focusDay.getDate(), 28)),
      PageDown: () => new Date(focusDay.getFullYear(), focusDay.getMonth() + 1, Math.min(focusDay.getDate(), 28)),
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    focusOn(move())
  }

  // Six fixed weeks keep the popover the same height from month to month.
  const gridStart = addDays(view, -view.getDay())
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index))
  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(view)
  const display = selected
    ? new Intl.DateTimeFormat('en-US', dateOnly ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }).format(selected)
    : dateOnly ? 'Select date' : 'Select date and time'

  return (
    <>
      <button
        aria-expanded={popover.isOpen}
        aria-haspopup="dialog"
        className="wt-select-trigger wt-date-trigger"
        data-open={popover.isOpen || undefined}
        id={id}
        onClick={() => (popover.isOpen ? popover.close() : openPicker())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !popover.isOpen) {
            event.preventDefault()
            openPicker()
          }
        }}
        ref={popover.triggerRef}
        type="button"
      >
        <span className="wt-select-value">
          <CalendarDays aria-hidden="true" size={15} />
          <span>{display}</span>
        </span>
        <ChevronDown aria-hidden="true" className="wt-select-chevron" size={15} />
      </button>

      {popover.phase !== 'closed'
        ? createPortal(
            <div
              aria-label={dateOnly ? 'Choose date' : 'Choose date and time'}
              className="wt-popover wt-date-panel"
              data-state={popover.phase}
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return
                event.preventDefault()
                event.stopPropagation()
                popover.close()
              }}
              ref={popover.panelRef}
              role="dialog"
              style={popover.style}
            >
              <div className="wt-date-header">
                <button
                  aria-label="Previous month"
                  className="wt-date-nav"
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  type="button"
                >
                  <ChevronLeft aria-hidden="true" size={16} />
                </button>
                <span aria-live="polite">{monthLabel}</span>
                <button
                  aria-label="Next month"
                  className="wt-date-nav"
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  type="button"
                >
                  <ChevronRight aria-hidden="true" size={16} />
                </button>
              </div>

              <div className="wt-date-grid" onKeyDown={onGridKeyDown} role="group" aria-label={monthLabel}>
                {WEEKDAYS.map((weekday) => (
                  <span aria-hidden="true" className="wt-date-weekday" key={weekday}>{weekday}</span>
                ))}
                {days.map((day) => {
                  const key = dayKey(day)
                  const isSelected = selected !== null && key === dayKey(selected)

                  return (
                    <button
                      aria-current={key === dayKey(today) ? 'date' : undefined}
                      aria-label={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      aria-pressed={isSelected}
                      className="wt-date-day"
                      data-day={key}
                      data-outside={day.getMonth() !== view.getMonth() || undefined}
                      key={key}
                      onClick={() => {
                        setFocusDay(day)
                        commit(withTime(day, hours, base.getMinutes()))
                        // Picking the day is the whole choice when there is no time.
                        if (dateOnly) popover.close()
                      }}
                      tabIndex={key === dayKey(focusDay) ? 0 : -1}
                      type="button"
                    >
                      {day.getDate()}
                    </button>
                  )
                })}
              </div>

              <div className={`wt-date-footer${dateOnly ? ' is-date-only' : ''}`}>
                {dateOnly ? null : (
                <div className="wt-time" role="group" aria-label="Time">
                  <TimeSegment
                    label="Hour"
                    max={12}
                    min={1}
                    onCommit={(next) => commit(withTime(base, (next % 12) + (pm ? 12 : 0), base.getMinutes()))}
                    value={hour12}
                  />
                  <span aria-hidden="true">:</span>
                  <TimeSegment
                    label="Minute"
                    max={59}
                    min={0}
                    onCommit={(next) => commit(withTime(base, hours, next))}
                    value={base.getMinutes()}
                  />
                  <div className="wt-time-period">
                    {(['AM', 'PM'] as const).map((period) => (
                      <button
                        aria-pressed={(period === 'PM') === pm}
                        key={period}
                        onClick={() => commit(withTime(base, hour12 % 12 + (period === 'PM' ? 12 : 0), base.getMinutes()))}
                        type="button"
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                <div className="wt-date-actions">
                  <button
                    className="lead-crm-action"
                    onClick={() => {
                      const now = new Date()
                      commit(withTime(now, hours, base.getMinutes()))
                      focusOn(now)
                    }}
                    type="button"
                  >
                    Today
                  </button>
                  {dateOnly ? null : (
                  <button
                    className="lead-crm-action"
                    onClick={() => {
                      const now = new Date()
                      commit(now)
                      focusOn(now)
                    }}
                    type="button"
                  >
                    Now
                  </button>
                  )}
                  <button className="button button-primary wt-date-done" onClick={() => popover.close()} type="button">
                    Done
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
